import { useEffect, useCallback } from 'react';

interface MediaSessionOptions {
  title: string;
  artist?: string;
  album?: string;
  artwork?: string;
  onPlay?: () => void;
  onPause?: () => void;
  onSeekBackward?: () => void;
  onSeekForward?: () => void;
  onPreviousTrack?: () => void;
  onNextTrack?: () => void;
}

/**
 * Hook to integrate with Media Session API for lock screen / notification controls
 * Works on iOS (Control Center, AirPlay) and Android (notification controls)
 */
export function useMediaSession(options: MediaSessionOptions | null) {
  const updateMediaSession = useCallback(() => {
    if (!options || !('mediaSession' in navigator)) return;

    const { title, artist, album, artwork } = options;

    // Set metadata
    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artist || 'Park Row',
      album: album || 'Sunny\'s Juke Joint',
      artwork: artwork ? [
        { src: artwork, sizes: '96x96', type: 'image/jpeg' },
        { src: artwork, sizes: '128x128', type: 'image/jpeg' },
        { src: artwork, sizes: '192x192', type: 'image/jpeg' },
        { src: artwork, sizes: '256x256', type: 'image/jpeg' },
        { src: artwork, sizes: '384x384', type: 'image/jpeg' },
        { src: artwork, sizes: '512x512', type: 'image/jpeg' },
      ] : [],
    });
  }, [options]);

  const setupActionHandlers = useCallback(() => {
    if (!options || !('mediaSession' in navigator)) return;

    const {
      onPlay,
      onPause,
      onSeekBackward,
      onSeekForward,
      onPreviousTrack,
      onNextTrack,
    } = options;

    // Set action handlers
    if (onPlay) {
      navigator.mediaSession.setActionHandler('play', onPlay);
    }
    if (onPause) {
      navigator.mediaSession.setActionHandler('pause', onPause);
    }
    if (onSeekBackward) {
      navigator.mediaSession.setActionHandler('seekbackward', onSeekBackward);
    }
    if (onSeekForward) {
      navigator.mediaSession.setActionHandler('seekforward', onSeekForward);
    }
    if (onPreviousTrack) {
      navigator.mediaSession.setActionHandler('previoustrack', onPreviousTrack);
    }
    if (onNextTrack) {
      navigator.mediaSession.setActionHandler('nexttrack', onNextTrack);
    }
  }, [options]);

  useEffect(() => {
    updateMediaSession();
    setupActionHandlers();

    return () => {
      if ('mediaSession' in navigator) {
        // Clean up action handlers
        try {
          navigator.mediaSession.setActionHandler('play', null);
          navigator.mediaSession.setActionHandler('pause', null);
          navigator.mediaSession.setActionHandler('seekbackward', null);
          navigator.mediaSession.setActionHandler('seekforward', null);
          navigator.mediaSession.setActionHandler('previoustrack', null);
          navigator.mediaSession.setActionHandler('nexttrack', null);
        } catch (e) {
          // Some browsers don't support setting null handlers
        }
      }
    };
  }, [updateMediaSession, setupActionHandlers]);

  // Update playback state
  const setPlaybackState = useCallback((state: 'playing' | 'paused' | 'none') => {
    if ('mediaSession' in navigator) {
      navigator.mediaSession.playbackState = state;
    }
  }, []);

  // Update position state for seek bar in notification
  const setPositionState = useCallback((duration: number, position: number, playbackRate = 1) => {
    if ('mediaSession' in navigator && navigator.mediaSession.setPositionState) {
      try {
        navigator.mediaSession.setPositionState({
          duration,
          position,
          playbackRate,
        });
      } catch (e) {
        // Position state not supported or invalid values
      }
    }
  }, []);

  return { setPlaybackState, setPositionState };
}
